import React from 'react';
import { push } from 'redux-first-history';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from '@mui/material/styles/styled';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { ROUTES } from '../../../../helpers/constants';

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...theme.mixins.toolbar, // necessary for content to be below app bar
}));

const Img = (props) => {
  const { routes, height } = props;
  const { pathname } = useLocation();
  const route = routes[0];

  const onPage = routes.map((route) => route.path).includes(pathname);

  return (
    <Box
      component='img'
      height={height}
      alt={`${route.title} Icon`}
      title={`${route.title} Icon`}
      src={
        onPage
          ? `/icons${route.path}-icon-white.png`
          : `/icons${route.path}-icon.png`
      }
    />
  );
};

const SectionHeader = (props) => {
  const { expanded, text } = props;

  if (!expanded) {
    return <Divider sx={{ my: 0.75, mx: 1.5 }} />;
  }

  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 0.25 }}>
      <ListItemText
        primary={text}
        slotProps={{
          primary: {
            variant: 'caption',
            color: 'text.disabled',
            sx: {
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
            },
          },
        }}
      />
    </Box>
  );
};

const DrawerItem = (props) => {
  const { expanded, icon, text, paths } = props;
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const onPage = paths.includes(pathname);

  const bgcolor = onPage ? 'primary.main' : 'unset';
  const textColor = onPage ? 'black' : 'grey';
  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        onClick={() => dispatch(push(paths[0]))}
        sx={{
          justifyContent: expanded ? 'initial' : 'center',
          py: 0.5,
          px: 1.25,
        }}
      >
        <Card
          sx={{
            p: 0.5,
            minHeight: 36,
            minWidth: 36,
            height: 36,
            width: 36,
            bgcolor,
            mr: expanded ? 1.5 : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ListItemIcon
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </ListItemIcon>
        </Card>
        {expanded && (
          <ListItemText
            primary={text}
            slotProps={{
              primary: { color: textColor },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};

function DrawerContent() {
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { drawerExpanded } = useSelector((state) => state.app);
  const expanded = drawerExpanded || isMobile;

  return (
    <Root>
      <DrawerHeader>
        {(isMobile || !drawerExpanded) && (
          <Box
            onClick={() => dispatch(push('/'))}
            height={35}
            component='img'
            alt='Orchestrate Icon'
            src='/icons/hub-icon.png'
            title='Orchestrate Icon'
          />
        )}
        {!isMobile && drawerExpanded && (
          <Box
            onClick={() => dispatch(push('/'))}
            height={27}
            component='img'
            alt='Orchestrate By Veregy'
            src='/orchestrate-logo.png'
            title='Orchestrate By Veregy'
          />
        )}
      </DrawerHeader>
      <Divider />
      <List disablePadding>
        <SectionHeader expanded={expanded} text='Admin' />

        <DrawerItem
          expanded={expanded}
          text={ROUTES.HOME.title}
          icon={
            <FontAwesomeIcon
              color={
                [ROUTES.INDEX.path, ROUTES.HOME.path].includes(
                  location.pathname
                )
                  ? 'white'
                  : 'inherit'
              }
              icon={['fal', 'home']}
              size='xl'
            />
          }
          paths={[ROUTES.INDEX.path, ROUTES.HOME.path]}
        />
        <DrawerItem
          expanded={expanded}
          text={ROUTES.ORGANIZATIONS.title}
          icon={
            <FontAwesomeIcon
              color={
                location.pathname === ROUTES.ORGANIZATIONS.path
                  ? 'white'
                  : 'inherit'
              }
              icon={['fal', 'sitemap']}
              size='xl'
            />
          }
          paths={[ROUTES.ORGANIZATIONS.path]}
        />
        <DrawerItem
          expanded={expanded}
          text={ROUTES.USERS.title}
          icon={
            <FontAwesomeIcon
              color={
                location.pathname === ROUTES.USERS.path ? 'white' : 'inherit'
              }
              icon={['fal', 'users']}
              size='xl'
            />
          }
          paths={[ROUTES.USERS.path]}
        />

        <SectionHeader expanded={expanded} text='Applications' />

        <DrawerItem
          expanded={expanded}
          text={ROUTES.EVENTSYNC.title}
          icon={<Img height={22} routes={[ROUTES.EVENTSYNC]} />}
          paths={[ROUTES.EVENTSYNC.path]}
        />
        <DrawerItem
          expanded={expanded}
          text={ROUTES.GRIDCAP.title}
          icon={<Img height={20} routes={[ROUTES.GRIDCAP]} />}
          paths={[ROUTES.GRIDCAP.path]}
        />
        <DrawerItem
          expanded={expanded}
          text={ROUTES.LIGHTLEVEL.title}
          icon={<Img height={22} routes={[ROUTES.LIGHTLEVEL]} />}
          paths={[ROUTES.LIGHTLEVEL.path]}
        />
        <DrawerItem
          expanded={expanded}
          text={ROUTES.ENERGYTRACER.title}
          icon={<Img height={20} routes={[ROUTES.ENERGYTRACER]} />}
          paths={[ROUTES.ENERGYTRACER.path]}
        />

        <SectionHeader expanded={expanded} text='Performance' />

        <DrawerItem
          expanded={expanded}
          text={ROUTES.AWS.title}
          icon={
            <FontAwesomeIcon
              color={
                location.pathname === ROUTES.AWS.path ? 'white' : 'inherit'
              }
              icon={['fab', 'aws']}
              size='xl'
            />
          }
          paths={[ROUTES.AWS.path]}
        />
      </List>
    </Root>
  );
}

export default DrawerContent;
